import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    address: '',
  });

  const { login, register, loginWithGoogle, axiosInstance } = useAuth();
  const navigate = useNavigate();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post('/auth/forgot-password', {
        email: forgotEmail,
        origin: window.location.origin,
      });
      toast.success('Si un compte existe avec cet email, un lien de réinitialisation a été envoyé.');
      setShowForgot(false);
    } catch (error) {
      toast.error('Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  const extractErrorMessage = (error) => {
    const translate = (msg) => {
      if (!msg || typeof msg !== 'string') return msg;
      const map = {
        'Invalid credentials': 'Email ou mot de passe incorrect',
        'Email already registered': 'Cet email est déjà associé à un compte. Connectez-vous ou utilisez un autre email.',
        'Not authenticated': 'Vous n\'êtes pas connecté.',
        'Internal Server Error': 'Le serveur rencontre un problème temporaire. Merci de réessayer dans quelques instants.',
      };
      if (map[msg]) return map[msg];
      if (msg.toLowerCase().includes('not a valid email')) return 'Adresse email invalide. Veuillez vérifier votre saisie.';
      return msg;
    };
    if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
      return 'Impossible de joindre le serveur. Vérifiez votre connexion Internet.';
    }
    const status = error?.response?.status;
    if (status === 500 || status === 502 || status === 503) {
      return 'Le serveur rencontre un problème temporaire. Merci de réessayer dans quelques instants.';
    }
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return translate(detail);
    if (Array.isArray(detail)) {
      const first = detail[0];
      if (first?.msg) return translate(first.msg);
    }
    if (error?.message) return error.message;
    return 'Une erreur est survenue';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Connexion réussie !');
      } else {
        await register(formData);
        toast.success('Compte créé avec succès !');
      }
      navigate('/menu');
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <main className="min-h-screen bg-transparent pt-24 pb-16">
      <div className="container-padding max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-8 shadow-card"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-navy mb-2">
              {showForgot ? 'Mot de passe oublié' : isLogin ? 'Bienvenue' : 'Créer un compte'}
            </h1>
            <p className="text-navy/60">
              {showForgot
                ? 'Entrez votre email pour recevoir un lien de réinitialisation'
                : isLogin
                ? 'Connectez-vous pour commander'
                : 'Rejoignez-nous pour profiter de nos plats'}
            </p>
          </div>

          {showForgot ? (
            <>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <input
                  type="email"
                  placeholder="Email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className="input-field w-full"
                  data-testid="forgot-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary disabled:opacity-50"
                  data-testid="forgot-submit-button"
                >
                  {loading ? 'Envoi...' : 'Envoyer le lien'}
                </button>
              </form>
              <p className="text-center mt-6 text-navy/60">
                <button
                  onClick={() => setShowForgot(false)}
                  className="text-terracotta font-medium hover:underline"
                  data-testid="back-to-login"
                >
                  Retour à la connexion
                </button>
              </p>
            </>
          ) : (
          <>
          {/* Google Login */}
          <button
            onClick={loginWithGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-stone-200 rounded-full py-3 px-6 font-medium text-navy hover:border-terracotta hover:bg-terracotta/5 transition-colors mb-6"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continuer avec Google
          </button>

          <div className="relative mb-6">
            <hr className="border-stone-200" />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-navy/50">
              ou
            </span>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    placeholder="Nom complet"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    className="input-field w-full"
                    data-testid="register-name-input"
                  />
                </div>
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Téléphone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field w-full"
                    data-testid="register-phone-input"
                  />
                </div>
                <div className="relative">
                  <input
                    type="text"
                    name="address"
                    placeholder="Adresse de livraison"
                    value={formData.address}
                    onChange={handleChange}
                    className="input-field w-full"
                    data-testid="register-address-input"
                  />
                </div>
              </>
            )}

            <div className="relative">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
                className="input-field w-full"
                data-testid="email-input"
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Mot de passe"
                value={formData.password}
                onChange={handleChange}
                required
                className="input-field w-full pr-12"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {isLogin && (
              <p className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  className="text-sm text-terracotta hover:underline"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50"
              data-testid="submit-button"
            >
              {loading ? 'Chargement...' : isLogin ? 'Se connecter' : "S'inscrire"}
            </button>
          </form>

          {/* Toggle */}
          <p className="text-center mt-6 text-navy/60">
            {isLogin ? "Pas encore de compte ?" : 'Déjà un compte ?'}{' '}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-terracotta font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "S'inscrire" : 'Se connecter'}
            </button>
          </p>
          </>
          )}
        </motion.div>
      </div>
    </main>
  );
}
